import { useEffect, useState, type ReactNode } from 'react'

import GuardLoadingScreen from './GuardLoadingScreen'
import MediaContext from './MediaGuardContext'

// Define the allowed breakpoint prefixes for type safety
export type Breakpoint = '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl'

// Map prefixes to their REM values
// IMPORTANT: these values must be kept in sync with the Tailwind CSS configuration
const breakpointMap: Record<Breakpoint, number> = {
  '2xs': 0,
  xs: 24,
  sm: 40,
  md: 48,
  lg: 64,
  xl: 80,
  '2xl': 96,
}

export type MediaQueryState = Record<Breakpoint, boolean>

interface MediaGuardProps {
  children: ReactNode
}

function matchBreakpoint(breakpoint: Breakpoint) {
  const remValue = breakpointMap[breakpoint]
  const query = `(min-width: ${remValue}rem)`
  return window.matchMedia(query)
}

function getInitialMatches(): MediaQueryState {
  const initialMatches = {} as MediaQueryState
  for (const key in breakpointMap) {
    initialMatches[key as Breakpoint] = matchBreakpoint(key as Breakpoint).matches
  }

  return initialMatches
}

function MediaGuard({ children }: MediaGuardProps) {
  const [matches, setMatches] = useState<MediaQueryState>(getInitialMatches())

  useEffect(() => {
    // The effect is now only responsible for subscribing to changes, not setting the initial state.
    const mediaQueries: Record<Breakpoint, MediaQueryList> = {} as Record<Breakpoint, MediaQueryList>
    const listeners: Record<Breakpoint, (event: MediaQueryListEvent) => void> = {} as Record<Breakpoint, (event: MediaQueryListEvent) => void>

    Object.keys(breakpointMap).forEach(key => {
      const breakpoint = key as Breakpoint
      const mediaQuery = matchBreakpoint(breakpoint)
      mediaQueries[breakpoint] = mediaQuery

      // Create a listener to update state when the media query match changes
      listeners[breakpoint] = (event: MediaQueryListEvent) => {
        setMatches(prev => ({
          ...prev,
          [breakpoint]: event.matches,
        }))
      }

      // Add the event listener
      mediaQuery.addEventListener('change', listeners[breakpoint])
    })

    // Cleanup function to remove all listeners when the component unmounts
    return () => {
      Object.keys(mediaQueries).forEach(key => {
        const breakpoint = key as Breakpoint
        mediaQueries[breakpoint].removeEventListener('change', listeners[breakpoint])
      })
    }
  }, [])

  const anyMatch = Object.values(matches).some(value => value)

  if (!anyMatch) {
    return <GuardLoadingScreen i18nKey="common.loading" />
  }

  return (
    <MediaContext.Provider value={{ matches }}>
      {children}
    </MediaContext.Provider>
  )
}

export default MediaGuard
